import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import { SEARCH_INDEX_LAG_MS } from '@/shared/features/quiz-list/utils/myQuizListCache';

import type { Folder } from '../types/folder.types';

import type { FolderCursorPage, FolderSearchPage } from './fetchers/folder';
import { folderKeys } from './folder.keys';
import type { MyFoldersVariables } from './folder.queries';
import { canPrependCreatedFolder } from './folderListReconcile';

/**
 * 폴더를 만들거나 지운 뒤 내 폴더 목록 캐시를 바로 맞춘다.
 *
 * 검색 인덱스가 늦어 바로 다시 받을 수 없다(`folderListReconcile`). 결과를 알고 있으므로 캐시를 직접 고쳐
 * 화면을 바로 바꾸고, 인덱스가 따라온 뒤 한 번 더 받아 개수·순서를 서버와 맞춘다(`myQuizListCache` 와 같은 규칙).
 */

type FolderSearchData = InfiniteData<FolderSearchPage, string | undefined>;
type FolderItemsData = InfiniteData<FolderCursorPage, string | undefined>;

/**
 * 만든 폴더를 목록 캐시에 넣는다.
 *
 * - 최신순 목록: 첫 페이지 맨 앞. 인기순: 자리를 모르므로 개수만. 검색어가 걸린 목록: 손대지 않는다.
 * - 저장 위치 모달이 읽는 목록(`listWithItems`): 맨 앞 — 만든 폴더를 바로 고를 수 있어야 한다.
 *
 * 진행 중인 조회를 먼저 끊는다 — 인덱스가 따라오기 전 결과가 넣은 폴더를 덮어쓰지 않게 한다.
 */
export const insertFolderIntoLists = async (
  queryClient: QueryClient,
  folder: Folder,
) => {
  await queryClient.cancelQueries({ queryKey: folderKeys.lists() });

  for (const [key, data] of queryClient.getQueriesData<FolderSearchData>({
    queryKey: folderKeys.listSearch(),
  })) {
    if (!data || data.pages.length === 0) continue;
    // react-query-kit 의 키는 `[...queryKey, variables]` 다.
    const variables = key.at(-1) as MyFoldersVariables | undefined;
    if (variables?.categoryFilters?.criteria?.searchText) continue;
    if (data.pages.some(page => page.items.some(item => item.id === folder.id)))
      continue;
    const shouldPrepend = canPrependCreatedFolder(variables);
    queryClient.setQueryData<FolderSearchData>(key, {
      ...data,
      pages: data.pages.map((page, index) => ({
        ...page,
        items:
          shouldPrepend && index === 0 ? [folder, ...page.items] : page.items,
        // 모든 페이지가 같은 전체 개수를 싣는다. 전부 올린다.
        totalCount:
          page.totalCount === undefined ? undefined : page.totalCount + 1,
      })),
    });
  }

  queryClient.setQueriesData<FolderItemsData>(
    { queryKey: folderKeys.listWithItems() },
    data => {
      if (!data || data.pages.length === 0) return data;
      if (
        data.pages.some(page => page.items.some(item => item.id === folder.id))
      )
        return data;
      return {
        ...data,
        pages: data.pages.map((page, index) =>
          index === 0 ? { ...page, items: [folder, ...page.items] } : page,
        ),
      };
    },
  );
};

/** 지운 폴더를 목록 캐시에서 뺀다. 개수(`N개`)도 함께 줄인다. */
export const removeFolderFromLists = (queryClient: QueryClient, id: string) => {
  queryClient.setQueriesData<FolderSearchData>(
    { queryKey: folderKeys.listSearch() },
    data => {
      // 검색어가 걸린 목록처럼 이 폴더가 애초에 없던 목록은 그대로 둔다.
      const isInList = data?.pages.some(page =>
        page.items.some(item => item.id === id),
      );
      if (!data || !isInList) return data;
      return {
        ...data,
        pages: data.pages.map(page => ({
          ...page,
          items: page.items.filter(item => item.id !== id),
          // 모든 페이지가 같은 전체 개수를 싣는다. 폴더가 어느 페이지에 있든 전부 줄인다.
          totalCount:
            page.totalCount === undefined
              ? undefined
              : Math.max(0, page.totalCount - 1),
        })),
      };
    },
  );
};

/** 인덱스가 따라온 뒤 목록을 한 번 더 받아 개수·순서를 서버와 맞춘다. */
export const refetchFolderListsAfterIndexLag = (queryClient: QueryClient) => {
  setTimeout(() => {
    void queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
  }, SEARCH_INDEX_LAG_MS);
};
